import type { FC } from 'react';

import { useCms } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { InputText, TextArea } from '@eneco/sparky';
import { useFormContext, useFormState } from 'react-hook-form';

import type { FormValues } from './types';
import { useFormField } from './useFormField';

/**
 * The shared body of every single-line/multi-line text field. Five block types
 * (`GenericFormInputField`, `…Email…`, `…PhoneNumber…`, `…Number…`, `…TextArea…`) differ only in
 * input type, so they compose this rather than repeating it.
 */
export const TextInputField: FC<{ role: string; fallbackName: string; type?: 'text' | 'email' | 'tel' | 'number'; multiline?: boolean }> = ({
  role,
  fallbackName,
  type = 'text',
  multiline = false,
}) => {
  const { adapter } = useCms();
  const { register } = useFormContext();
  const { errors } = useFormState<FormValues>();

  const field = useFormField('genericFields', role);
  const name = field.name || fallbackName;
  const hint = adapter.toRichText(field.hint);
  const Component = multiline ? TextArea : InputText;

  return (
    <Component
      error={errors?.[name]?.message as string | undefined}
      hint={hint ? <RichText html={hint} /> : undefined}
      label={field.label ?? ''}
      placeholder={field.placeholder}
      {...(multiline ? {} : { type })}
      {...register(name)}
    />
  );
};
